// Analytics Page
// Refactored to use modular components from /components/analytics/

import { useState, useEffect } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

// Import all analytics components
import {
  AnalyticsSidebar,
  AnalyticsHeader,
  OverviewMetrics,
  ConversionFunnelChart,
  LeadScoreChart,
  TrendsChart,
  AccidentTypesChart,
  LeadSourcesList,
  LeadsByDayChart,
  LeadsByHourChart,
  ResponseTimesChart
} from '@/components/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AnalyticsPage = () => {
  const { logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dateRange, setDateRange] = useState('30');
  
  // Analytics data states
  const [overview, setOverview] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [trends, setTrends] = useState(null);
  const [leadScores, setLeadScores] = useState(null);
  const [accidentTypes, setAccidentTypes] = useState(null);
  const [leadSources, setLeadSources] = useState(null);
  const [timeAnalysis, setTimeAnalysis] = useState(null);
  const [responseTimes, setResponseTimes] = useState(null);

  const fetchAllAnalytics = async () => {
    try {
      const days = parseInt(dateRange);
      
      const [
        overviewRes,
        funnelRes,
        trendsRes,
        scoresRes,
        typesRes,
        sourcesRes,
        timeRes,
        responseRes
      ] = await Promise.all([
        axios.get(`${API}/analytics/overview?days=${days}`),
        axios.get(`${API}/analytics/conversion-funnel?days=${days}`),
        axios.get(`${API}/analytics/trends?days=${days}`),
        axios.get(`${API}/analytics/lead-scores?days=${days}`),
        axios.get(`${API}/analytics/accident-types?days=${days}`),
        axios.get(`${API}/analytics/lead-sources?days=${days}`),
        axios.get(`${API}/analytics/time-analysis?days=${days}`),
        axios.get(`${API}/analytics/response-times?days=${days}`)
      ]);
      
      setOverview(overviewRes.data);
      setFunnel(funnelRes.data);
      setTrends(trendsRes.data);
      setLeadScores(scoresRes.data);
      setAccidentTypes(typesRes.data);
      setLeadSources(sourcesRes.data);
      setTimeAnalysis(timeRes.data);
      setResponseTimes(responseRes.data);
    } catch (error) {
      // Silently handle - components will show empty state
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchAllAnalytics();
  }, [dateRange]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchAllAnalytics();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <AnalyticsSidebar logout={logout} activePath="/analytics" />

      <main className="flex-1 overflow-auto">
        <AnalyticsHeader 
          dateRange={dateRange}
          setDateRange={setDateRange}
          onRefresh={handleRefresh}
          refreshing={refreshing}
        />

        <div className="p-8 space-y-8">
          {/* Overview Metrics */}
          <OverviewMetrics overview={overview} />

          {/* Conversion Funnel & Lead Score Distribution */}
          <div className="grid lg:grid-cols-2 gap-6">
            <ConversionFunnelChart funnel={funnel} />
            <LeadScoreChart leadScores={leadScores} />
          </div>

          {/* Trends Chart */}
          <TrendsChart trends={trends} />

          {/* Accident Types & Lead Sources */}
          <div className="grid lg:grid-cols-2 gap-6">
            <AccidentTypesChart accidentTypes={accidentTypes} />
            <LeadSourcesList leadSources={leadSources} />
          </div>

          {/* Time Analysis */}
          <div className="grid lg:grid-cols-2 gap-6">
            <LeadsByDayChart timeAnalysis={timeAnalysis} />
            <LeadsByHourChart timeAnalysis={timeAnalysis} />
          </div>

          {/* Response Time Analysis */}
          <ResponseTimesChart responseTimes={responseTimes} />
        </div>
      </main>
    </div>
  );
};

export default AnalyticsPage;
